/**
 * @name        Helper
 * @description Helpers act as a registry for specific types of Javascript modules
 *              that are to be made available to a project runtime.  Helpers exist to provide
 *              common behavior, interfaces, and life cycle events and hooks for all types of things
 *              such as servers, compilers, commands, or anything else that can be categorized and grouped.
 *              Helpers are designed to be shared across multiple projects, and there are event Project Type helpers
 *              which exist to make specific types of helpers available to specific types of projects.
 */

import { hideProperty, lazy, mixinPropertyUtils } from 'skypager-util/lib/properties'
import { camelize, underscore, singularize, pluralize } from 'skypager-util/lib/string'
import ContextRegistry from 'skypager-registry/lib/context'
import hashObject from 'object-hash'
import configBuilder from './config'
import attachEmitter from './emitter'

const lodash = require('lodash')
const { lodashModules } = require('./lodash-modules')

const utils = require('./util')
const {
  flatten,
  castArray,
  isUndefined,
  partialRight,
  mapValues,
  pickBy,
  isFunction,
  isObject,
  omitBy,
  defaults,
  defaultsDeep,
  omit,
  result,
  keys,
  has,
} = lodash

const req = require.context('./helpers', false, /DISABLED\.js$/)

let REGISTRY

REGISTRY = REGISTRY || new ContextRegistry('helpers', {
  context: req,
  componentWillRegister(...args) {
    return args
  }
})

export class Helper {
  static registry = REGISTRY

  /**
   * Helper classes can specify attributes that individual helper modules are
   * expected to provide or export.
   */
  static get providerTypes() {
    return { }
  }

  /**
   * Helper classes can specify options or parameters that can be passed in at the time
   * the helper instance is created.
   */
  static get optionTypes() {
    return {
      id: 'string',
      provider: 'object'
    }
  }

  /**
   * Helpers are always passed a context property from the host project or runtime's sandbox,
   * this will include a reference to the host project, as well as the registry the helper belongs to
   * and things such as the environment or process argv.
   */
  static get contextTypes() {
    return {
      project: 'object',
      reg: 'object',
      host: 'object'
    }
  }

  /**
   * Individual helper modules can reference the provdier type configuration from their constructor, and can override
   * them by passing in a providerTypes object in their options, by exporting a providerTypes object themselves.
   */
  get providerTypes() {
    return defaults({}, this.tryResult('providerTypes', {}), this.constructor.providerTypes, Helper.providerTypes)
  }

  /**
   * Individual helper modules can reference the options type configuration from their constructor, and can override
   * them by passing in optionTypes object in their options, by exporting a optionTypes object themselves.
   */
  get optionTypes() {
    return defaults({}, this.tryResult('optionTypes', {}), this.constructor.optionTypes, Helper.optionTypes)
  }

  /**
   * Individual helper modules can reference the context type configuration from their constructor, and can override
   * them by passing in a contextTypes object in their options, by exporting a contextTypes object themselves.
   */
  get contextTypes() {
    return defaults({}, {[this.registryName]: this.constructor.registry}, this.tryResult('contextTypes', {}), this.constructor.contextTypes, Helper.contextTypes)
  }

  /**
   * A Helper class is attached to a host.
   */
  static attach(host, helperClass, options) {
    return attach(host, helperClass, options)
  }

  static attachAll (host, options = {}) {
    if (!this.isHostValid(host)) {
      throw new Error('Invalid host for the helper registry. pass a project or a portfolio')
    }

    Helper.allHelperTypes.forEach(helperType => {
      helperType.attach(host, options)
    })

    return host
  }

  static isHostSupported (host) {
    return this.isHostValid(host)
  }

  static isHostValid (host) {
    return typeof host.hide === 'function' && typeof host.lazy === 'function'
  }

  static cacheable(setting = true) {
    return this.isCacheable = !!setting
  }

  static get allHelpers() {
    return Helper.allHelperTypes
  }

  static get allHelperTypes() {
    return Helper.registry.available
      .map(id => Helper.registry.lookup(id))
      .map(mod => mod.default ? mod.default : mod)
  }

  static get modules() {
    return Helper
  }

  static createContextRegistry(...args) {
    return new ContextRegistry(...args)
  }

  static createRegistry(...args) {
    return new ContextRegistry(...args)
  }

  static willCreateHelper(host, opts) { }

  static didCreateHelper(host, helperInstance, opts) { }

  isInitialized = false
  isConfigured = false

  constructor(options = {}, context = {}) {
    mixinPropertyUtils(this, true, true, lodashModules(lodash))
    attachEmitter(this)

    this.lazy('name', () => this.get('options.name', this.result('provider.name', () => options.name)), true)

    this.hide('uuid', require('uuid')())

    this.hide('context', context)

    try { this.hideGetter(`is${this.constructor.name}`, () => true) } catch(error) { }

    this.hide('registryName', Helper.propNames(this.constructor.name).registryProp)

    // these are all aliases
    this.hideGetter('project', () => context.project || context.host || context.runtime)
    this.hideGetter('host', () => context.project || context.host || context.runtime)
    this.hideGetter('runtime', () => context.project || context.host || context.runtime)

    if (this.project.beforeHelperCreate) {
      this.project.beforeHelperCreate.call(this.project, this, options, context, this.constructor)
    }

    this.hideGetter('options', () => omit({ ...this.defaultOptions, ...options, }, 'provider'))
    this.hide('provider', options.provider)

    this.lazy('id', () => [ this.get('project.id', this.constructor.name), options.name, Math.floor(new Date() / 100) ].join(':'))


    if (options.initialize !== false) {
      this.doInitialize()
    }

    this.lazy('configHistory', () => ([]), true)
  }

  get isCached() {
    return !!this.get('options.cacheable') && (this.cacheKey && this.cacheKey.length > 0)
  }

  get cacheKey() {
    return this.get('options.cacheKey')
  }

  /** */
  async doInitialize() {
    const initializer = this.tryGet('initialize', this.initialize)

    if (initializer) {
      await Promise.resolve(initializer.call(this, this.options, this.context))
      this.isInitialized = true
    }
  }

  fireHook(hookName, ...args) {
    this.emit(hookName, ...args)
    this.attemptMethod(hookName, { args, })
  }

  get chain() {
    return lodash.chain(this)
  }

  /**
   * Access the first value we find in our options hash in our provider hash
   */
  tryGet(property, defaultValue, sources = ['options', 'provider']) {
    return this.at(...sources.map(s => `${s}.${property}`)).find(v => !isUndefined(v)) || defaultValue
  }

  /**
   * Access the first value we find in our options hash in our provider hash
   *
   * If the method is a function, it will be called in the scope of the helper,
   * with the helpers options and context
   */
  tryResult(property, defaultValue, options = {}, context = {}) {
    const val = this.tryGet(property)

    if (!val) {
      return typeof defaultValue === 'function'
        ? defaultValue.call(this, {...this.options, ...options}, {...this.context, ...context})
        : defaultValue
    } else if (typeof val === 'function') {
      return val.call(this, {...this.options, ...options}, {...this.context, ...context})
    } else {
      return val
    }
  }

  // Merge the objects found at k starting with at options, provider, projectConfig
  mergeGet(key, namespaces = ['options', 'provider', 'projectConfig']) {
    key = typeof key === 'string' ? key.split('.') : key
    key = flatten(castArray(key))

    return defaults({}, ...namespaces.map(n => this.get([n, ...key])) )
  }

  // Merge the objects found at k starting with at options, provider, projectConfig
  // If the property is a function, it will be called in the scope of the helper, with the helpers options and context
  mergeResult(key, namespaces = ['options', 'provider', 'projectConfig']) {
    key = typeof key === 'string' ? key.split('.') : key
    key = flatten(castArray(key))

    const ifFunc = (v) => typeof v === 'function' ? v.call(this, this.options, this.context) : v

    return defaults({}, ...namespaces.map(n => this.get([n, ...key]) ).map(ifFunc))
  }

  static configFeatures() {
    return {}
  }

  static configReducers() {
    return {}
  }

  getConfigFeatures() {
    let providers = this.get('provider.configFeatures', function () { })
    let options = this.get('options.configFeatures', function() { })
    let constructors = this.get('constructor.configFeatures', function(){})

    providers = isFunction(providers) ? providers.call(this, this.options, this.context) : providers
    options = isFunction(options) ? options.call(this, this.options, this.context) : options
    constructors = isFunction(constructors) ? constructors.call(this, this.options, this.context) : constructors

    return mapValues(defaults(
      {},
      options,
      providers,
      constructors,
    ), (fn) => fn.bind(this))
  }

  getConfigReducers() {
    let providers = this.get('provider.configReducers', function () { })
    let options = this.get('options.configReducers', function() { })
    let constructors = this.get('constructor.configReducers', function(){})

    providers = isFunction(providers) ? providers.call(this, this.options, this.context) : providers
    options = isFunction(options) ? options.call(this, this.options, this.context) : options
    constructors = isFunction(constructors) ? constructors.call(this, this.options, this.context) : constructors

    return mapValues(defaults(
      {},
      options,
      providers,
      constructors,
    ), (fn) => fn.bind(this))
  }

  configurator(options = {}) {
    if (this._builder) { return this._builder }

    const {
      baseConfig = this.tryGet('baseConfig', {}),
      scope = this,
      tap = this.tryGet('tapConfig'),
    } = options

    const features = { ...this.getConfigFeatures(), ...(options.features || {}) }
    const reducers = { ...this.getConfigReducers(), ...(options.reducers || {}) }

    return this._builder = configBuilder({ features, reducers, history: this.configHistory, scope, tap, baseConfig })
  }

  configureWith(fn) {
    const builder = this._builder = fn(this.configurator())
    this.configHistory = builder.history
    return this
  }

  get currentConfig() {
    return this.configurator().getConfig()
  }

  stringifyConfig() {
    return this.currentConfig.toString()
  }

  static registryName() {
    return Helper.propNames(this.name).registryProp
  }

  static createMixin(methods = {}, target, ...partialArgs) {
    const functions = pickBy(methods, isFunction)
    const partialed = mapValues(functions, (fn) => partialRight(fn.bind(target), ...partialArgs))

    return mapValues(partialed, (boundFn) =>
      (options = {}, ...args) => boundFn(options, ...args)
    )
  }

  createMixin(methods = {}, context = this.context, target = this) {
    const functions = pickBy(methods, isFunction)
    const partialed = mapValues(functions, (fn) => partialRight(fn.bind(target), context))

    return mapValues(partialed, (boundFn) =>
      (options = {}, ...args) => boundFn(options, ...args)
    )
  }

  applyMixin(methods = this.provider, context = this.context, target) {
    return Object.assign(this, this.createMixin(methods, context, target))
  }

  mixin(methods = this.provider, context = this.context, target = this) {
    return this.applyMixin(methods, context, target)
  }

  attemptMethod(methodName, ...args) {
    const handler = this.tryGet(methodName, this.get(methodName))

    if (typeof handler === 'undefined') {
      if (this.project.isDevelopment && this.project.get('environment.DEBUG_HELPERS')) {
        this.project.debug(`attemptMethod called on non-existent method: ${methodName} `, { name: this.name, id: this.id })
      }

      return false
    }


    if (typeof handler === 'function') {
      if ( args.length === 0 ) { args.unshift({}) }
      args.push(this.context)

      return handler.call(this, ...args)
    }

    return handler
  }

  async attemptMethodAsync(name, ...args) {
    return await this.attemptMethod.call(this, name, ...args)
  }

  callMethod(methodName, ...args) {
    const handler = this.tryGet(methodName)

    if (typeof handler !== 'function') {
      throw new Error(`Could not find a property at ${methodName}`)
    }

    if (args.length === 0) {
      args.unshift({})
    }

    return handler.call(this, ...[...args, this.context])
  }

  invokeMethod(methodName, ...args) {
    console.log('invokeMethod IS DEPRECATED', methodName, this.id)
    const handler = this.get(methodName, this.tryGet(methodName))

    if (typeof handler === 'function') {
      args.push(this.context)
      return handler.call(this, ...args)
    } else if (typeof handler === 'undefined') {
      throw new Error(`Could not find a property at ${methodName}`)
    } else {
      return handler
    }
  }

  get invalidOptionKeys() {
    return this.chain.get('options').omit(Object.keys(this.optionTypes)).keys().value()
  }

  get invalidContextKeys() {
    return this.chain.get('context').omit(Object.keys(this.contextTypes)).keys().value()
  }

  get defaultOptions() {
    return defaultsDeep({}, this.argv, this.projectConfig)
  }

  get projectOptions() {
    return this.projectConfig
  }

  get projectConfig() {
    if (this.get('project.getOption')) {
      return this.project.getOption(`${this.registryName}.${this.name}`, {})
    } else if (this.get('project.options')) {
      return this.get('project.options')
    }
  }

  get argv() {
    return omit(
      this.get('project.argv', this.get('host.argv', {})),
      '_', ''
    )
  }

  static propNames(name = '') {
    return {
      registryProp: camelize(pluralize(underscore(name))).toLowerCase(),
      lookupProp: singularize(camelize(underscore(name))).toLowerCase(),
    }
  }

  static createHost(...args) {
    const host = utils.createHost(...args)

    Helper.attachAll(host)

    return host
  }

  static createMockContext(object = {}) {
    const fn = (key) => result(object, key, () => {
      throw new Error(`Module ${key} not found in mock context`)
    })

    return Object.assign(fn, {
      keys() {
        return keys(object)
      },
      resolve(key) {
        const resolved = (has(object, key) && key)

        if (resolved) {
          return resolved
        } else {
          throw new Error(`Module ${key} not found in mock context`)
        }
      },
    })
  }
}

export default Helper

export const createHost = Helper.createHost
export const createMockContext = Helper.createMockContext

export const registry = Helper.registry

export function attach (host, helperClass, options = {}) {
  const { registryProp, lookupProp, configKey = 'options' } = {
    ...Helper.propNames(helperClass.name),
    ...options,
  }

  if (host[registryProp]) {
    console.log('host already has', registryProp, host)
    return host
  }

  if (host.willAttachHelpers) {
    host.willAttachHelpers.call(host, helperClass, options)
  }

  lazy(host, registryProp, () => options.registry || helperClass.createRegistry())

  // ensures that we can always access the function
  hideProperty(host[registryProp], 'createHelperByName', (name, opts = {}, ctx = {}) => {
    const reg = host[registryProp]

    const {
      cacheHelper = !!(helperClass.isCacheable || opts.cacheHelper)
    } = opts

    opts = defaults(
      opts,
      {name, id: name, cacheHelper: !!helperClass.isCacheable },
      omit(host.argv, '', '_'),
      host.get(`${configKey}.${registryProp}.${name}`),
    )

    const helperContext = { ...host.createSandbox(ctx), host, reg, [registryProp]: reg }
    const provider = reg.lookup(name)
    const cacheable = !!(cacheHelper !== false && provider.isCacheable !== false && provider.cacheable !== false && typeof host.cache !== 'undefined')
    const cacheKey = [registryProp, hashObject(omitBy(opts, cacheableKeys)), name].join(':')

    opts.cacheKey = cacheKey
    opts.cacheable = cacheable
    opts.provider = provider

    if (host.willCreateHelper) {
      const response = host.willCreateHelper.call(host, opts)

      if (response === false) {
        return false
      }
    }

    if (helperClass.willCreateHelper(host, opts) === false) {
      return false
    }

    const helperInstance = cacheable
      ? host.cache.fetch(cacheKey, () => new helperClass(opts, helperContext))
      : new helperClass(opts, helperContext)

    if (opts.createGetter) {
      host.lazy(opts.createGetter, () => helperInstance, true)
    }

    helperInstance.hide('destroyHelper', () => {
      try {
        helperInstance.removeAllListeners()
        host.cache.delete(cacheKey)
      } catch(e) { }

      helperInstance.fireHook('willBeDestroyed', helperInstance, host, opts)
      return true
    })

    if (host.didCreateHelper) {
      host.didCreateHelper.call(host, helperInstance, opts)
    }

    helperClass.didCreateHelper(host, helperInstance, opts)

    return helperInstance
  })

  hideProperty(
    host[registryProp],
    'createLookup',
    (defaultOptions = {}, cacheHelper = helperClass.isCacheable) =>
      (id, options = {}, ...args) =>
        host[registryProp].createHelperByName(id, {...defaultOptions, cacheHelper, ...options}, ...args)
  )

  host[lookupProp] = (...args) => host[registryProp].createHelperByName(...args)

  if (host.didAttachHelpers) {
    host.didAttachHelpers.call(host, helperClass, options)
  }

  return host
}

const cacheableKeys = (value = {}, key) =>
  isFunction(value) || (value && value.then && isFunction(value.then)) || key == 'provider' || key === 'compiler'



// WEBPACK FOOTER //
// ./src/helper.js